import { Box, Button } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';

import { FormErrorLogger } from '@/components/Form/FormErrorLogger';
import { Select } from '@/components/Form/Select';
import { TextInput } from '@/components/Form/TextInput';
import { ActionBar } from '@/components/Layout';

import { FUNDING_SOURCE_MAX_LENGTH } from '@/constants/places';
import { getFundingSourceOptions } from '@/constants/user';

import { useAppSelector } from '@/hooks/redux';
import { useQuestionInfoMutation } from '@/hooks/useQuestionInfoMutation';
import { useTranslate } from '@/hooks/useTranslate';

import { selectUserData } from '@/redux/user-data/selectors';

import { FundingSource } from '@/types/user';

import { getDataTestId } from '@/utils/browser';

import { QuestionProps } from './QuestionDrawer.interface';
import { QuestionForm } from './QuestionForm';

export function QuestionAboutFundingSource({
  onAnswered,
  ...props
}: Readonly<QuestionProps>) {
  const { t } = useTranslate();
  const { user } = useAppSelector(selectUserData);
  const {
    handleSubmit,
    control,
    formState: { errors: visibleErrors, isValid },
  } = useForm<{
    fundingSource: string;
    otherFundingSource: string;
  }>({
    defaultValues: {
      fundingSource: '',
      otherFundingSource: '',
    },
    mode: 'onChange',
  });

  const { mutate, isPending, error } = useQuestionInfoMutation({
    onFinished: onAnswered,
    target: props.pepAnswerTarget,
  });

  const onSubmit = handleSubmit(async payload => {
    mutate({
      fundingSource:
        payload.fundingSource === FundingSource.OtherIncome
          ? payload.otherFundingSource
          : payload.fundingSource,
    });
  });

  const fundingSourceOptions = getFundingSourceOptions(user?.tenant);

  return (
    <QuestionForm
      {...props}
      isLoading={isPending}
      answerError={error}
      onSubmit={onSubmit}
    >
      <Select
        options={fundingSourceOptions}
        control={control}
        name="fundingSource"
        error={!!visibleErrors.fundingSource}
        helperText={visibleErrors.fundingSource?.message}
        rules={{
          required: t(
            'questionDrawer.aboutFundingSource.fundingSource.required',
          ),
        }}
        fullWidth
        placeholder={t(
          'questionDrawer.aboutFundingSource.fundingSource.placeHolder',
        )}
        useDrawerOnMobile
        label={t('questionDrawer.aboutFundingSource.fundingSource.label')}
        disabled={isPending}
      />
      <Controller
        control={control}
        name="fundingSource"
        render={({ field: { value } }) =>
          value === FundingSource.OtherIncome ? (
            <Box mt={2}>
              <TextInput
                name="otherFundingSource"
                control={control}
                placeholder={t(
                  'questionDrawer.aboutFundingSource.otherFundingSource.placeHolder',
                )}
                error={!!visibleErrors.otherFundingSource}
                helperText={visibleErrors.otherFundingSource?.message}
                rules={{
                  required: t(
                    'questionDrawer.aboutFundingSource.otherFundingSource.required',
                  ),
                  maxLength: {
                    value: FUNDING_SOURCE_MAX_LENGTH.otherFundingSource,
                    message: t(
                      'questionDrawer.aboutFundingSource.otherFundingSource.maxLength',
                      {
                        number: FUNDING_SOURCE_MAX_LENGTH.otherFundingSource,
                      },
                    ),
                  },
                }}
                disabled={isPending}
              />
            </Box>
          ) : (
            <></>
          )
        }
      />
      <ActionBar>
        <Button
          {...getDataTestId(`basic-info-confirm-button`)}
          variant="outlined"
          fullWidth
          type="submit"
          disabled={!isValid || isPending}
        >
          {t('questionDrawer.aboutFundingSource.button.proceed')}
        </Button>
      </ActionBar>
      <FormErrorLogger
        formName="QuestionAboutFundingSource"
        control={control}
      />
    </QuestionForm>
  );
}
